/**
 * Graphics quality presets. One preset drives view distance, resolution and
 * every optional effect, so "is it fast enough" has a single knob (G in game).
 */

export type PresetName = 'fast' | 'fancy' | 'ultra';

export interface Quality {
  name: PresetName;
  label: string;
  /** Chunks meshed around the player (radius of a disc). */
  viewRadius: number;
  /** Upper bound on devicePixelRatio (render scale). */
  pixelRatioCap: number;
  /** MSAA samples on the HDR scene target (0 = off). */
  msaa: number;
  bloom: boolean;
  /**
   * Sun shadow cascades: 0 (off), 2 or 3. Off in every preset: vanilla has no
   * shadow maps at any graphics level — shade comes from the skylight lightmap,
   * smooth-lighting AO and the fixed per-face shade.
   */
  shadowCascades: number;
  shadowMapSize: number;
  /** Fresnel sky reflections, ripples and sun glint on water. */
  waterFx: boolean;
  /** Wind sway on leaves and plants. */
  sway: boolean;
  /** Biome-blend radius: 0 = 1x1 (off), 1 = 3x3 (fast), 2 = 5x5 (fancy/ultra). */
  biomeBlend: 0 | 1 | 2;
}

export const PRESETS: Record<PresetName, Quality> = {
  fast: {
    name: 'fast', label: 'Fast', viewRadius: 8, pixelRatioCap: 1, msaa: 0, bloom: false,
    shadowCascades: 0, shadowMapSize: 1024, waterFx: false, sway: false, biomeBlend: 1,
  },
  fancy: {
    name: 'fancy', label: 'Fancy', viewRadius: 12, pixelRatioCap: 1.5, msaa: 4, bloom: true,
    shadowCascades: 0, shadowMapSize: 2048, waterFx: true, sway: true, biomeBlend: 2,
  },
  ultra: {
    name: 'ultra', label: 'Ultra', viewRadius: 16, pixelRatioCap: 2, msaa: 4, bloom: true,
    shadowCascades: 0, shadowMapSize: 2048, waterFx: true, sway: true, biomeBlend: 2,
  },
};

const ORDER: PresetName[] = ['fast', 'fancy', 'ultra'];
const STORAGE_KEY = 'opencraft.quality';

export function loadPreset(): Quality {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as PresetName | null;
    if (saved && PRESETS[saved]) return PRESETS[saved];
  } catch { /* storage unavailable — use the default */ }
  return PRESETS.fancy;
}

export function nextPreset(current: Quality): Quality {
  const next = PRESETS[ORDER[(ORDER.indexOf(current.name) + 1) % ORDER.length]];
  try {
    localStorage.setItem(STORAGE_KEY, next.name);
  } catch { /* ignore */ }
  return next;
}

/**
 * Misc app preferences persisted across sessions (debug HUD state, ...).
 * Kept separate from the world save so "New world" keeps them.
 */
export interface Prefs {
  /** Shift+3 debug overlay + full perf HUD on at startup. */
  debug: boolean;
}

const PREFS_KEY = 'opencraft.prefs';

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Prefs>;
      return { debug: p.debug === true };
    }
  } catch { /* storage unavailable */ }
  return { debug: false };
}

export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch { /* ignore */ }
}

/**
 * Sound category volumes (0..1), as vanilla's "Music & Sounds" screen.
 * Read/written by src/audio/volumes.ts; kept here so all persisted prefs live together.
 */
const SOUND_KEY = 'opencraft.sound';

export function loadSoundVolumes(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SOUND_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Record<string, unknown>;
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(o)) if (typeof v === 'number' && v >= 0 && v <= 1) out[k] = v;
      return out;
    }
  } catch { /* storage unavailable */ }
  return {};
}

export function saveSoundVolumes(v: Record<string, number>): void {
  try {
    localStorage.setItem(SOUND_KEY, JSON.stringify(v));
  } catch { /* ignore */ }
}
